import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

// Components
import FavoriteIcon from '@icons/FavoriteIcon';
import MailIcon from '@icons/MailIcon';
import ShareIcon from '@icons/ShareIcon';
import { PropertyChip } from '../PropertyChip';

// Functionality
import getFavoriteHook from '@hooks/getFavoriteHook';
import useAuthorized from '@hooks/useAuthorized';

const PropertyMapCard = ({
    property,
    isFavorite: initialIsFavorite,
    showShareIcon = false,
    favorited,
    onClick = () => {},
}) => {
    const { recordId } = property;
    const useFavorite = getFavoriteHook({ type: 'property', initialIsFavorite });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite, setIsFavoriteManually } = useFavorite();
    const { isLoggedIn } = useAuthorized();

    useEffect(() => {
        setIsFavoriteManually(initialIsFavorite);
    }, [initialIsFavorite, setIsFavoriteManually]);

    const onFavorite = () => {
        if (!isFavorite) {
            saveFavorite(recordId);
            favorited({ hit: property });
            return;
        }

        if (isFavorite) {
            removeFavorite(recordId);
        }
    };

    return (
        <div
            key={property.objectID}
            style={{
                boxShadow: '0 1px 6px 0 rgb(0 0 0 / 15%)',
                marginBottom: '1rem',
                padding: '1rem',
            }}
        >
            <div>
                <div className="d-flex justify-content-between">
                    <PropertyChip
                        activatedAt={property.activatedAt}
                        expiresAt={property.expiresAt}
                        activations={property.activations}
                    />

                    <div className="d-flex align-items-center">
                        {showShareIcon && (
                            <div className="mr-1rem">
                                <ShareIcon title={property.title} url={property.internalUrl} />
                            </div>
                        )}

                        <div hidden>
                            <MailIcon title={property.title} url={property.internalUrl} />
                        </div>

                        <FavoriteIcon
                            id={recordId}
                            type="property"
                            isLoggedIn={isLoggedIn}
                            isFavorite={isFavorite}
                            onClick={onFavorite}
                            isLoading={isLoadingFavorite}
                        />
                    </div>
                </div>

                <Link
                    to={property.internalUrl}
                    className="card-link"
                    onClick={e => {
                        e.preventDefault();
                        window.open(property.internalUrl, '_blank');
                        onClick({ hit: property });
                    }}
                >
                    <h3
                        className="display6 bold mt-3 mb-2"
                        style={{
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {property.title}
                    </h3>
                    <div className="mb-2">
                        <div
                            style={{
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {property.displayLocation}
                        </div>
                    </div>
                    <div className="text-gray-dark">
                        <div>{property.purpose}</div>
                        <div>{property.populationDensity}</div>
                        <div>{property.typeOfCenter}</div>
                        <div>
                            {property.squareFeet && <>{property.squareFeet.toLocaleString()} sq ft</>}
                            {!property.squareFeet && <>{property.size}</>}
                        </div>
                    </div>
                </Link>
            </div>
        </div>
    );
};

PropertyMapCard.propTypes = {
    property: PropTypes.shape({
        objectID: PropTypes.string,
        recordId: PropTypes.number,
        activations: PropTypes.number.isRequired,
        title: PropTypes.string,
        internalUrl: PropTypes.string,
        displayLocation: PropTypes.string,
        purpose: PropTypes.string,
        populationDensity: PropTypes.string,
        typeOfCenter: PropTypes.string,
        size: PropTypes.string,
        squareFeet: PropTypes.number,
        isFavorite: PropTypes.bool,
        onClick: PropTypes.func,
        activatedAt: PropTypes.string,
        expiresAt: PropTypes.string,
    }),
    isFavorite: PropTypes.bool,
    showShareIcon: PropTypes.bool,
    favorited: PropTypes.func,
    onClick: PropTypes.func,
};

export default PropertyMapCard;
